import React, { useState } from 'react';
import { motion } from 'framer-motion';
import logger from '../utils/logger';
import { useRBAC } from '../contexts/RBACContext';
import { 
  Users, 
  MousePointer, 
  Mail, 
  MessageSquare, 
  TrendingUp, 
  Phone,
  Target,
  Activity,
  Zap,
  Clock,
  RefreshCw,
  Layers,
  Send,
  Reply,
  Calendar,
  Eye,
  Filter,
  Archive
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { useDashboardData } from '../hooks/useDashboardData';

const Dashboard = () => {
  // RBAC hook for role-based visibility
  const { isAdminOrHigher } = useRBAC();
  
  // Utility function to get current date in Finnish timezone (Europe/Helsinki)
  const getFinnishDate = () => {
    const now = new Date();
    const finnishTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Helsinki' }));
    return finnishTime.toISOString().split('T')[0];
  };

  // Use database-driven dashboard data with RBAC built-in
  const {
    loading: isLoading,
    stats,
    automationStats,
    leadStatusDistribution,
    engagementTimeline,
    automationTimeline,
    topCampaigns,
    recentActivity,
    dailyStats,
    zeroOpensCount,
    todayActivityStats,
    timelineFilter,
    setTimelineFilter,
    refresh,
    fetchDailyStats,
    archiveDailyStats,
    fetchRecentActivityForDate
  } = useDashboardData();
  
  // Local UI state - Initialize with Finnish timezone date
  const [selectedDate, setSelectedDate] = useState(getFinnishDate());
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [viewMode, setViewMode] = useState('today');
  const [localDailyStats, setLocalDailyStats] = useState(null);
  const [localRecentActivity, setLocalRecentActivity] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [maxDate, setMaxDate] = useState(getFinnishDate());

  // Load daily stats and activity for a specific date (limit to 5 activities)
  const loadDailyStatsForDate = async (date) => {
    try {
      // Fetch both stats and activity for the selected date (limit activity to 5)
      const [dateStats, dateActivity] = await Promise.all([
        fetchDailyStats(date),
        fetchRecentActivityForDate(date, 5)
      ]);
      
      setLocalDailyStats(dateStats);
      setLocalRecentActivity(dateActivity);
      setSelectedDate(date);
      logger.log(`✅ Loaded stats and activity for ${date} (showing 5 recent activities)`);
    } catch (error) {
      logger.error('Error loading daily stats for date:', error);
    }
  };

  // Reset to today's data (using Finnish timezone)
  const resetToToday = async () => {
    const today = getFinnishDate();
    setViewMode('today');
    setLocalDailyStats(null);
    setLocalRecentActivity(null);
    setSelectedDate(today);
    setMaxDate(today);
    logger.log(`✅ Reset to today's live data (Finnish time: ${today})`);
  };

  // Archive daily stats to permanent storage
  const handleArchiveStats = async (date) => {
    try {
      const success = await archiveDailyStats(date);
      if (success) {
        alert(`Stats for ${date} have been archived to permanent storage.`);
      } else {
        alert('Failed to archive stats. Please try again.');
      }
    } catch (error) {
      logger.error('Error archiving stats:', error);
      alert('Error archiving stats. Please try again.');
    }
  };
  
  // Refresh all data
  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await refresh();
      logger.log('Dashboard data refreshed at:', new Date().toISOString());
    } catch (error) {
      logger.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Use data from hook - all processing done in database
  const eventTimelineData = engagementTimeline;
  const leadStatusData = leadStatusDistribution;
  const campaignData = topCampaigns;
  
  // Use dailyStats from hook or local override
  const currentDailyStats = localDailyStats || dailyStats;

  const StatCard = ({ title, value, icon: Icon, change, color = 'primary', subtitle, badge }) => {
    const colorClasses = {
      primary: {
        bg: 'bg-gradient-to-br from-blue-50/70 via-blue-50/30 to-indigo-50/50',
        iconBg: 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700',
        iconShadow: 'shadow-blue-500/20',
        text: 'text-blue-700',
        accent: 'text-blue-600',
        border: 'border-blue-100/50',
        glow: 'shadow-blue-100/40'
      },
      blue: {
        bg: 'bg-gradient-to-br from-blue-50/70 via-blue-50/30 to-blue-100/50',
        iconBg: 'bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700',
        iconShadow: 'shadow-blue-500/20',
        text: 'text-blue-700',
        accent: 'text-blue-600',
        border: 'border-blue-100/50',
        glow: 'shadow-blue-100/40'
      }
    };

    const currentColor = colorClasses[color];

    return (
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        whileHover={{ 
          scale: 1.015, 
          y: -1,
          transition: { duration: 0.15, ease: "easeOut" }
        }}
        whileTap={{ scale: 0.985 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className={`
          bg-white/95 backdrop-blur-md rounded-xl shadow-md hover:shadow-lg 
          border ${currentColor.border} p-4 transition-all duration-250 
          relative overflow-hidden group cursor-pointer
          hover:${currentColor.glow} hover:shadow-xl
        `}
      >
        {/* Compact background gradient */}
        <div className={`
          absolute inset-0 ${currentColor.bg} opacity-40 
          group-hover:opacity-60 transition-all duration-250
          group-hover:scale-102 origin-center
        `} />
        
        <div className="relative flex items-start justify-between h-full">
          <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
            {/* Compact header section */}
            <div className="mb-2">
              <div className="flex items-center gap-1.5 mb-1.5">
                <h3 className={`text-xs font-bold ${currentColor.text} truncate tracking-wide uppercase`}>
                  {title}
                </h3>
                {badge && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold bg-white/90 text-gray-700 rounded-md border border-gray-200/70 shadow-sm backdrop-blur-sm">
                    {badge}
                  </span>
                )}
              </div>
              
              {/* Compact main value */}
              <div className="flex items-baseline gap-1">
                <p className="text-2xl font-black text-gray-900 leading-none tracking-tight">
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </p>
                {change && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15 }}
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-bold ${
                      change > 0 
                        ? 'bg-emerald-100/90 text-emerald-700 border border-emerald-200/70' 
                        : change < 0 
                          ? 'bg-red-100/90 text-red-700 border border-red-200/70'
                          : 'bg-gray-100/90 text-gray-700 border border-gray-200/70'
                    } shadow-sm backdrop-blur-sm`}>
                    <TrendingUp className={`w-2.5 h-2.5 mr-0.5 ${
                      change < 0 ? 'rotate-180' : ''
                    }`} />
                    {change > 0 ? '+' : ''}{change}%
                  </motion.div>
                )}
              </div>
            </div>
            
            {/* Compact subtitle section */}
            {subtitle && (
              <p className="text-xs font-medium text-gray-600 leading-tight mt-auto">
                {subtitle}
              </p>
            )}
          </div>
          
          {/* Compact blue-themed icon */}
          <motion.div 
            whileHover={{ 
              scale: 1.08, 
              rotate: 3,
              transition: { duration: 0.15 }
            }}
            className={`
              w-11 h-11 rounded-xl flex items-center justify-center 
              ${currentColor.iconBg} ${currentColor.iconShadow} shadow-md
              group-hover:shadow-lg transition-all duration-250
              relative overflow-hidden flex-shrink-0
            `}
          >
            {/* Icon glow effect */}
            <div className="absolute inset-0 bg-white/15 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-250" />
            <Icon className="w-5.5 h-5.5 text-white relative z-10" strokeWidth={2.5} />
          </motion.div>
        </div>
        
        {/* Subtle bottom accent */}
        <div className={`
          absolute bottom-0 left-3 right-3 h-px 
          bg-gradient-to-r from-transparent via-gray-200 to-transparent
          opacity-25 group-hover:opacity-40 transition-opacity duration-250
        `} />
      </motion.div>
    );
  };

  return (
    <div className="py-4 lg:py-6 space-y-6 max-w-full">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <div className="flex-1">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent mb-1">
            Dashboard
          </h1>
          <p className="text-sm text-gray-600 font-medium">Real-time insights into your leads and email automation performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50/80 border border-blue-200/60 rounded-lg backdrop-blur-sm">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            <span className="text-xs font-semibold text-blue-700">LIVE</span>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleRefreshAll}
            disabled={isLoading || refreshing}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-4 py-2 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-1.5 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${(isLoading || refreshing) ? 'animate-spin' : ''}`} strokeWidth={2.5} />
            {isLoading || refreshing ? 'Updating...' : 'Refresh'}
          </motion.button>
        </div>
      </motion.div>

      {/* Enhanced Stats Cards Grid - Optimized for 6 Cards */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-4"
      >
        <StatCard
          title="Total Leads"
          value={stats.totalLeads}
          icon={Users}
          color="primary"
          subtitle={`${stats.callListLeads} in call list`}
        />
        <StatCard
          title="Total Emails Sent"
          value={(stats.totalManualSent || 0) + (automationStats.totalEmails || 0)}
          icon={Mail}
          color="blue"
          subtitle={`${stats.totalManualSent || 0} manual + ${automationStats.totalEmails || 0} automation`}
        />
        <StatCard
          title="Total Opens"
          value={stats.opens || 0}
          icon={MousePointer}
          color="primary"
          subtitle={`${zeroOpensCount || 0} zero opens • ${stats.openRate || 0}% open rate`}
        />
        <StatCard
          title="Total Replies"
          value={stats.replies}
          icon={Reply}
          color="blue"
          subtitle={`${stats.replyRate || 0}% reply rate`}
        />
        <StatCard
          title="Active Campaigns"
          value={stats.activeCampaigns || automationStats.activeCampaigns || 0}
          icon={Target}
          color="primary"
          subtitle={`${stats.campaigns} total campaigns`}
        />
        <StatCard
          title="Follow-up Calls"
          value={stats.followUpCalls || automationStats.followUpCalls || 0}
          icon={Phone}
          color="blue"
          subtitle="Pending manual outreach"
        />
      </motion.div>

      {/* Optimized Charts Layout with Perfect Spacing */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}
        className="grid grid-cols-1 xl:grid-cols-2 gap-5"
      >
        {/* Lead Engagement Timeline - Enhanced Compact Modern Design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, ease: "easeOut" }}
          className="bg-white/98 backdrop-blur-lg rounded-2xl shadow-xl border border-blue-100/80 p-5 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group min-h-[480px]"
        >
          {/* Enhanced Blue Theme Background with Compact Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-white/70 to-blue-100/40 opacity-60 group-hover:opacity-80 transition-opacity duration-200" />
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-xl" />
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-300/20 to-transparent rounded-full blur-lg" />
          
          <div className="relative h-full flex flex-col">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                  <TrendingUp className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0 tracking-tight">
                    Lead Engagement Timeline
                  </h3>
                  <p className="text-xs text-gray-600 font-medium">
                    {timelineFilter === 'current_month' ? 'Current month activity' : 'Past 6 months activity'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-blue-100/80 to-blue-50/80 px-2.5 py-1 rounded-full border border-blue-200/60 backdrop-blur-sm">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold text-blue-700">LIVE</span>
              </div>
            </div>
            
            {/* Compact Timeline Filter Controls */}
            <div className="flex gap-1.5 mb-4 p-2 bg-gradient-to-r from-blue-50/70 via-white/80 to-blue-50/70 rounded-lg border border-blue-100/60 shadow-inner backdrop-blur-sm">
              <button
                onClick={() => setTimelineFilter('current_month')}
                className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-all duration-200 ${
                  timelineFilter === 'current_month'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'bg-white/80 text-blue-700 hover:bg-blue-50/80 border border-blue-200/50 shadow-sm'
                }`}
              >
                Current Month
              </button>
              <button
                onClick={() => setTimelineFilter('past_6_months')}
                className={`flex-1 px-3 py-1.5 text-xs font-bold rounded-md transition-all duration-200 ${
                  timelineFilter === 'past_6_months'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'bg-white/80 text-blue-700 hover:bg-blue-50/80 border border-blue-200/50 shadow-sm'
                }`}
              >
                Past 6 Months
              </button>
            </div>

            {/* Compact Legend */}
            <div className="flex justify-center gap-6 mb-3 p-2.5 bg-gradient-to-r from-white/90 via-blue-50/20 to-white/90 rounded-lg border border-blue-100/60 shadow-sm backdrop-blur-sm">
              <motion.div 
                className="flex items-center gap-1.5 group/legend"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.15 }}
              >
                <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-sm"></div>
                <span className="text-xs font-bold text-gray-800">Opens</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-1.5 group/legend"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.15 }}
              >
                <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full shadow-sm"></div>
                <span className="text-xs font-bold text-gray-800">Total Sent</span>
              </motion.div>
              <motion.div 
                className="flex items-center gap-1.5 group/legend"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.15 }}
              >
                <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-300 to-blue-400 rounded-full shadow-sm"></div>
                <span className="text-xs font-bold text-gray-800">Replies</span>
              </motion.div>
            </div>

            {/* Enhanced Chart Container */}
            <div className="h-64 w-full bg-gradient-to-br from-white/95 to-blue-50/20 rounded-lg p-2.5 border border-blue-100/50 shadow-inner backdrop-blur-sm">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={eventTimelineData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 20,
                  }}
                >
                  <defs>
                    <linearGradient id="opensGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#60A5FA" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="repliesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#93C5FD" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#93C5FD" stopOpacity={0.1}/>
                    </linearGradient>
                    <filter id="chartShadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#1e40af" floodOpacity="0.1"/>
                    </filter>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#E5E7EB" 
                    opacity={0.5}
                    strokeWidth={0.8}
                  />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ 
                      fontSize: 11, 
                      fill: '#6B7280', 
                      fontWeight: '600',
                      fontFamily: 'Inter, system-ui, sans-serif'
                    }}
                    tickMargin={8}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ 
                      fontSize: 11, 
                      fill: '#6B7280', 
                      fontWeight: '600',
                      fontFamily: 'Inter, system-ui, sans-serif'
                    }}
                    tickMargin={8}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      backdropFilter: 'blur(8px)',
                      fontSize: '12px',
                      fontWeight: '600',
                      padding: '10px 12px',
                      fontFamily: 'Inter, system-ui, sans-serif'
                    }}
                    labelStyle={{
                      color: '#1F2937',
                      fontWeight: '700',
                      marginBottom: '6px',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif'
                    }}
                    itemStyle={{
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '12px',
                      fontFamily: 'Inter, system-ui, sans-serif'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="opens"
                    stackId="1"
                    stroke="#3B82F6"
                    strokeWidth={2.5}
                    fill="url(#opensGradient)"
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4, filter: 'url(#chartShadow)' }}
                    activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2, fill: '#ffffff', filter: 'url(#chartShadow)' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalSent"
                    stackId="1"
                    stroke="#60A5FA"
                    strokeWidth={2.5}
                    fill="url(#clicksGradient)"
                    dot={{ fill: '#60A5FA', strokeWidth: 2, r: 4, filter: 'url(#chartShadow)' }}
                    activeDot={{ r: 6, stroke: '#60A5FA', strokeWidth: 2, fill: '#ffffff', filter: 'url(#chartShadow)' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="replies"
                    stackId="1"
                    stroke="#93C5FD"
                    strokeWidth={2.5}
                    fill="url(#repliesGradient)"
                    dot={{ fill: '#93C5FD', strokeWidth: 2, r: 4, filter: 'url(#chartShadow)' }}
                    activeDot={{ r: 6, stroke: '#93C5FD', strokeWidth: 2, fill: '#ffffff', filter: 'url(#chartShadow)' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        {/* Email Automation Performance - Enhanced Compact Modern Design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, ease: "easeOut" }}
          className="bg-white/98 backdrop-blur-lg rounded-2xl shadow-xl border border-blue-100/80 p-5 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group min-h-[480px]"
        >
          {/* Compact Blue Theme Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-white/70 to-blue-100/40 opacity-60 group-hover:opacity-80 transition-opacity duration-200" />
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-xl" />
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-300/20 to-transparent rounded-full blur-lg" />
          
          <div className="relative h-full flex flex-col">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-200">
                  <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-0 tracking-tight">
                    Email Automation Performance
                  </h3>
                  <p className="text-xs text-gray-600 font-medium">Last 7 days activity</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-blue-100/80 to-blue-50/80 px-2.5 py-1 rounded-full border border-blue-200/60 backdrop-blur-sm">
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold text-blue-700">LIVE</span>
              </div>
            </div>
            
            {/* Compact Automation Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
              {/* Card 1: Email Sequences */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-white/95 via-blue-50/30 to-white/95 p-2.5 rounded-lg border border-blue-200/60 backdrop-blur-sm hover:shadow-md transition-all duration-200 group/stat relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-40 group-hover/stat:opacity-60 transition-opacity duration-150" />
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center shadow-sm group-hover/stat:scale-105 transition-transform duration-150">
                      <Layers className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-xs font-bold text-blue-800">Email Sequences</span>
                  </div>
                  <div className="text-xl font-bold text-blue-900 mb-1">{automationStats.totalSequences}</div>
                  <div className="text-xs text-blue-700 font-bold bg-gradient-to-r from-blue-100/70 to-blue-50/70 px-1.5 py-0.5 rounded border border-blue-200/50 shadow-sm">
                    {automationStats.activeSequences} active
                  </div>
                </div>
              </motion.div>
              
              {/* Card 2: Automation Opens */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-white/95 via-blue-50/30 to-white/95 p-2.5 rounded-lg border border-blue-200/60 backdrop-blur-sm hover:shadow-md transition-all duration-200 group/stat relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-40 group-hover/stat:opacity-60 transition-opacity duration-150" />
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center shadow-sm group-hover/stat:scale-105 transition-transform duration-150">
                      <Eye className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-xs font-bold text-blue-800">Opens</span>
                  </div>
                  <div className="text-xl font-bold text-blue-900 mb-1">{automationStats.emailOpens}</div>
                  <div className="text-xs text-blue-700 font-bold bg-gradient-to-r from-blue-100/70 to-blue-50/70 px-1.5 py-0.5 rounded border border-blue-200/50 shadow-sm">
                    {automationStats.automationOpenRate || 0}% rate
                  </div>
                </div>
              </motion.div>

              {/* Card 3: Total Executions */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-white/95 via-blue-50/30 to-white/95 p-2.5 rounded-lg border border-blue-200/60 backdrop-blur-sm hover:shadow-md transition-all duration-200 group/stat relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-40 group-hover/stat:opacity-60 transition-opacity duration-150" />
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center shadow-sm group-hover/stat:scale-105 transition-transform duration-150">
                      <Activity className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-xs font-bold text-blue-800">Total Executions</span>
                  </div>
                  <div className="text-xl font-bold text-blue-900 mb-1">{automationStats.totalExecutions}</div>
                </div>
              </motion.div>
               
              {/* Card 4: Emails Sent */}
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-gradient-to-br from-white/95 via-blue-50/30 to-white/95 p-2.5 rounded-lg border border-blue-200/60 backdrop-blur-sm hover:shadow-md transition-all duration-200 group/stat relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-40 group-hover/stat:opacity-60 transition-opacity duration-150" />
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md flex items-center justify-center shadow-sm group-hover/stat:scale-105 transition-transform duration-150">
                      <Send className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="text-xs font-bold text-blue-800">Emails Sent</span>
                  </div>
                  <div className="text-xl font-bold text-blue-900 mb-1">{automationStats.totalEmails}</div>
                  <div className="flex items-center gap-1 text-xs">
                    <Clock className="w-3 h-3 text-orange-600" strokeWidth={2.5} />
                    <span className="font-bold text-orange-700">{automationStats.pendingExecutions}</span>
                    <span className="text-orange-600 font-semibold">processing</span>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Enhanced Dynamic Automation Timeline Chart - Flex Grow */}
            <div className="flex-1">
              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9, duration: 0.4, ease: "easeOut" }}
                className="bg-gradient-to-br from-white/98 via-blue-50/20 to-white/98 rounded-xl p-3 border border-blue-200/60 backdrop-blur-lg shadow-lg relative overflow-hidden h-full group/chart"
              >
                {/* Dynamic Background Effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/25 via-transparent to-blue-100/15 opacity-40 group-hover/chart:opacity-60 transition-opacity duration-300" />
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-2xl animate-pulse" />
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-blue-300/25 to-transparent rounded-full blur-xl" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-r from-blue-100/10 to-blue-200/10 rounded-full blur-3xl opacity-30" />
                
                {/* Chart Legend */}
                <div className="relative mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-sm"></div>
                      <span className="text-xs font-semibold text-blue-800">Sequences</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full shadow-sm"></div>
                      <span className="text-xs font-semibold text-emerald-800">Emails</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 bg-gradient-to-r from-violet-500 to-violet-600 rounded-full shadow-sm"></div>
                      <span className="text-xs font-semibold text-violet-800">Replies</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-gradient-to-r from-blue-100/70 to-blue-50/70 px-2 py-1 rounded-full border border-blue-200/50">
                    <Activity className="w-3 h-3 text-blue-600" strokeWidth={2.5} />
                    <span className="text-xs font-bold text-blue-700">Timeline</span>
                  </div>
                </div>
                
                <div className="relative h-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={automationTimeline}
                      margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
                    >
                      <defs>
                        {/* Enhanced Gradients for Lines */}
                        <linearGradient id="sequencesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#1E40AF" stopOpacity={0.9}/>
                        </linearGradient>
                        <linearGradient id="emailsGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10B981" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#047857" stopOpacity={0.9}/>
                        </linearGradient>
                        <linearGradient id="repliesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#6D28D9" stopOpacity={0.9}/>
                        </linearGradient>
                        
                        {/* Enhanced Shadow Filter */}
                        <filter id="chartShadowAutoEnhanced" x="-50%" y="-50%" width="200%" height="200%">
                          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#3B82F6" floodOpacity="0.2"/>
                          <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#ffffff" floodOpacity="0.8"/>
                        </filter>
                        
                        {/* Glow Effect Filter */}
                        <filter id="glowEffect" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                          <feMerge> 
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                          </feMerge>
                        </filter>
                      </defs>
                      
                      <CartesianGrid 
                        strokeDasharray="3 4" 
                        stroke="#E5E7EB" 
                        opacity={0.4}
                        horizontal={true}
                        vertical={false}
                      />
                      
                      <XAxis 
                        dataKey="date" 
                        tick={{ 
                          fontSize: 11, 
                          fill: '#6B7280', 
                          fontWeight: 600,
                          fontFamily: 'Inter, system-ui, sans-serif'
                        }}
                        axisLine={{ stroke: '#D1D5DB', strokeWidth: 1.5 }}
                        tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                        tickMargin={12}
                        height={60}
                        interval={0}
                      />
                      
                      <YAxis 
                        tick={{ 
                          fontSize: 11, 
                          fill: '#6B7280', 
                          fontWeight: 600,
                          fontFamily: 'Inter, system-ui, sans-serif'
                        }}
                        axisLine={{ stroke: '#D1D5DB', strokeWidth: 1.5 }}
                        tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                        tickMargin={8}
                        width={60}
                      />
                      
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                          border: '1px solid rgba(59, 130, 246, 0.25)',
                          borderRadius: '12px',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          backdropFilter: 'blur(12px)',
                          fontSize: '12px',
                          fontWeight: '600',
                          padding: '12px 14px',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          margin: '0'
                        }}
                        labelStyle={{ 
                          color: '#1F2937', 
                          fontWeight: '700',
                          fontSize: '13px',
                          marginBottom: '8px'
                        }}
                        cursor={{ stroke: '#3B82F6', strokeWidth: 1, strokeDasharray: '4 4', opacity: 0.5 }}
                      />
                      
                      {/* Enhanced Line Components with Dynamic Effects */}
                      <Line 
                        type="monotone" 
                        dataKey="sequences" 
                        stroke="url(#sequencesGradient)"
                        strokeWidth={3.5}
                        dot={{ 
                          fill: '#3B82F6', 
                          strokeWidth: 3, 
                          r: 5,
                          filter: 'url(#chartShadowAutoEnhanced)'
                        }}
                        activeDot={{ 
                          r: 8, 
                          stroke: '#3B82F6', 
                          strokeWidth: 3, 
                          fill: '#ffffff',
                          filter: 'url(#glowEffect)',
                          style: { cursor: 'pointer' }
                        }}
                        connectNulls={false}
                      />
                      
                      <Line 
                        type="monotone" 
                        dataKey="emails" 
                        stroke="url(#emailsGradient)"
                        strokeWidth={3.5}
                        dot={{ 
                          fill: '#10B981', 
                          strokeWidth: 3, 
                          r: 5,
                          filter: 'url(#chartShadowAutoEnhanced)'
                        }}
                        activeDot={{ 
                          r: 8, 
                          stroke: '#10B981', 
                          strokeWidth: 3, 
                          fill: '#ffffff',
                          filter: 'url(#glowEffect)',
                          style: { cursor: 'pointer' }
                        }}
                        connectNulls={false}
                      />
                      
                      <Line 
                        type="monotone" 
                        dataKey="replied" 
                        stroke="url(#repliesGradient)"
                        strokeWidth={3.5}
                        dot={{ 
                          fill: '#8B5CF6', 
                          strokeWidth: 3, 
                          r: 5,
                          filter: 'url(#chartShadowAutoEnhanced)'
                        }}
                        activeDot={{ 
                          r: 8, 
                          stroke: '#8B5CF6', 
                          strokeWidth: 3, 
                          fill: '#ffffff',
                          filter: 'url(#glowEffect)',
                          style: { cursor: 'pointer' }
                        }}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Status Distribution and Recent Activity */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
        className="grid grid-cols-1 xl:grid-cols-2 gap-4"
      >
        {/* Lead Status Distribution - Advanced Clean Design with Perfect Alignment */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.5, ease: [0.23, 1, 0.32, 1], duration: 0.5 }}
          className="bg-gradient-to-br from-white/98 via-blue-50/20 to-indigo-50/30 backdrop-blur-xl rounded-2xl shadow-xl border border-blue-100/60 p-5 hover:shadow-2xl transition-all duration-400 relative overflow-hidden group min-h-[420px]"
          style={{
            boxShadow: '0 20px 40px -12px rgba(59, 130, 246, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.04)'
          }}
        >
          {/* Clean Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-300/3 via-indigo-200/5 to-purple-300/3 opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-200/15 to-transparent rounded-full blur-xl" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-indigo-200/10 to-transparent rounded-full blur-lg" />
          
          <div className="relative z-10 h-full flex flex-col">
            {/* Clean Header Design */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300"
                  style={{
                    boxShadow: '0 6px 20px -6px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <Users className="w-5 h-5 text-white" strokeWidth={2.5} />
                </motion.div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-0.5 tracking-tight">
                    Lead Status Distribution
                  </h3>
                  <p className="text-xs text-gray-600 font-medium">Real-time analytics</p>
                </div>
              </div>
              <motion.div 
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500/8 to-indigo-500/8 px-3 py-1.5 rounded-xl border border-blue-200/40 backdrop-blur-sm shadow-sm"
              >
                <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-bold text-blue-700">LIVE</span>
              </motion.div>
            </div>
            
            {/* Perfect Pie Chart Container - Flex Grow */}
            <div className="bg-gradient-to-br from-white/95 via-blue-50/30 to-indigo-50/20 rounded-xl p-3 border border-blue-100/50 shadow-sm backdrop-blur-sm mb-4 relative overflow-hidden flex-1">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-transparent opacity-40" />
              <div className="relative z-10 h-full">
                <div className="h-full w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <defs>
                        <filter id="cleanPieShadow" x="-30%" y="-30%" width="160%" height="160%">
                          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1e40af" floodOpacity="0.12"/>
                          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#3b82f6" floodOpacity="0.08"/>
                        </filter>
                      </defs>
                      <Pie
                        data={leadStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                        filter="url(#cleanPieShadow)"
                        animationBegin={0}
                        animationDuration={1000}
                        animationEasing="ease-out"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {leadStatusData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color}
                            stroke="rgba(255, 255, 255, 0.9)"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08))'
                            }}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 16px 24px -4px rgba(0, 0, 0, 0.08), 0 8px 8px -4px rgba(0, 0, 0, 0.03)',
                          backdropFilter: 'blur(12px)',
                          fontSize: '12px',
                          fontWeight: '600',
                          padding: '10px 14px',
                          fontFamily: 'Inter, system-ui, sans-serif'
                        }}
                        labelStyle={{
                          color: '#1F2937',
                          fontWeight: '700',
                          marginBottom: '6px',
                          fontSize: '13px'
                        }}
                        itemStyle={{
                          color: '#374151',
                          fontWeight: '600',
                          fontSize: '12px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Enhanced Stats Grid - 4 Cards Layout */}
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              {/* Total Leads Card */}
              <motion.div
                initial={{ opacity: 0, y: 15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 250, damping: 20 }}
                whileHover={{ scale: 1.01, y: -1 }}
                className="bg-gradient-to-br from-white/95 via-blue-50/40 to-indigo-50/20 p-3 rounded-lg border border-blue-100/50 backdrop-blur-sm hover:shadow-md transition-all duration-300 group/stat relative overflow-hidden"
                style={{
                  boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.02)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 opacity-30 group-hover/stat:opacity-50 transition-opacity duration-200" />
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1.5">
                    <motion.div 
                      animate={{ scale: [1, 1.08, 1] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                      className="w-2.5 h-2.5 rounded-full shadow-sm bg-gradient-to-r from-blue-500 to-indigo-500"
                      style={{ 
                        boxShadow: `0 0 6px rgba(59, 130, 246, 0.4)`
                      }}
                    ></motion.div>
                    <span className="text-xs font-bold text-gray-800 capitalize tracking-wide">
                      Total Leads
                    </span>
                  </div>
                  <div className="text-xl font-black text-blue-900 mb-1.5">
                    {stats.totalLeads}
                  </div>
                  <div className="inline-flex items-center gap-1 text-xs font-bold bg-gradient-to-r from-blue-500/8 to-indigo-500/8 text-blue-700 px-2 py-0.5 rounded-md border border-blue-200/40 shadow-sm backdrop-blur-sm">
                    <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                    100%
                  </div>
                </div>
              </motion.div>
              
              {leadStatusData.map((status, index) => (
                <motion.div
                  key={status.name}
                  initial={{ opacity: 0, y: 15, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.6 + (index + 1) * 0.08, type: "spring", stiffness: 250, damping: 20 }}
                  whileHover={{ scale: 1.01, y: -1 }}
                  className="bg-gradient-to-br from-white/95 via-blue-50/40 to-indigo-50/20 p-3 rounded-lg border border-blue-100/50 backdrop-blur-sm hover:shadow-md transition-all duration-300 group/stat relative overflow-hidden"
                  style={{
                    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-indigo-50/20 opacity-30 group-hover/stat:opacity-50 transition-opacity duration-200" />
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-1.5">
                      <motion.div 
                        animate={{ scale: [1, 1.08, 1] }}
                        transition={{ duration: 2.5, repeat: Infinity, delay: (index + 1) * 0.3 }}
                        className="w-2.5 h-2.5 rounded-full shadow-sm"
                        style={{ 
                          backgroundColor: status.color,
                          boxShadow: `0 0 6px ${status.color}30`
                        }}
                      ></motion.div>
                      <span className="text-xs font-bold text-gray-800 capitalize tracking-wide">
                        {status.name.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-xl font-black text-blue-900 mb-1.5">
                      {status.value}
                    </div>
                    <div className="inline-flex items-center gap-1 text-xs font-bold bg-gradient-to-r from-blue-500/8 to-indigo-500/8 text-blue-700 px-2 py-0.5 rounded-md border border-blue-200/40 shadow-sm backdrop-blur-sm">
                      <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                      {((status.value / leadStatusData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, ease: "easeOut" }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-md border border-gray-200/60 p-5 hover:shadow-lg transition-all duration-200 relative overflow-hidden group"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-blue-500/10 opacity-60 group-hover:opacity-80 transition-opacity duration-200" />
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                  <Activity className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
                  <p className="text-xs text-gray-600 font-medium">Latest lead interactions and events</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Filter className="w-3 h-3" />
                  Filter
                </button>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-semibold text-gray-700">LIVE</span>
                </div>
              </div>
            </div>

            {/* Daily Stats Summary - Only visible for Admin/Developer */}
            {isAdminOrHigher() && (viewMode === 'today' ? dailyStats : localDailyStats) && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <h4 className="text-sm font-semibold text-gray-900">
                      {viewMode === 'today' ? 'Today\'s Activity' : `Activity for ${selectedDate}`}
                    </h4>
                  </div>
                  {viewMode !== 'today' && (
                    <button
                      onClick={resetToToday}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 hover:text-green-700 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Back to Today
                    </button>
                  )}
                  {viewMode !== 'today' && (
                    <button
                      onClick={() => handleArchiveStats(selectedDate)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      <Archive className="w-3 h-3" />
                      Archive
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-4">
                  {(() => {
                    const displayStats = viewMode === 'today' ? dailyStats : localDailyStats;
                    return (
                      <>
                        <div className="text-center">
                          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mb-2 mx-auto">
                            <Send className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="text-lg font-bold text-gray-900">{displayStats.totalSent}</div>
                          <div className="text-xs text-gray-600">Sent</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {displayStats.manualSent}M + {displayStats.automationSent}A
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-lg mb-2 mx-auto">
                            <Eye className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="text-lg font-bold text-gray-900">{displayStats.totalOpens}</div>
                          <div className="text-xs text-gray-600">Opens</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {displayStats.uniqueLeadsOpened} unique
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg mb-2 mx-auto">
                            <Reply className="w-4 h-4 text-purple-600" />
                          </div>
                          <div className="text-lg font-bold text-gray-900">{displayStats.totalReplies}</div>
                          <div className="text-xs text-gray-600">Replies</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {displayStats.uniqueLeadsReplied} unique
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-lg mb-2 mx-auto">
                            <MousePointer className="w-4 h-4 text-orange-600" />
                          </div>
                          <div className="text-lg font-bold text-gray-900">{displayStats.totalClicks}</div>
                          <div className="text-xs text-gray-600">Clicks</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {displayStats.uniqueLeadsClicked} unique
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Date Filter Panel */}
            {showDateFilter && (
              <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-4 mb-3">
                  <button
                    onClick={() => {
                      resetToToday();
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      viewMode === 'today' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Today (Finnish Time)
                  </button>
                  <button
                    onClick={() => setViewMode('date')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      viewMode === 'date' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Specific Date
                  </button>
                </div>
                {viewMode === 'date' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={selectedDate}
                      max={maxDate}
                      onChange={(e) => loadDailyStatsForDate(e.target.value)}
                      className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500">(Max: {maxDate})</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-3">
              {(() => {
                const displayActivity = viewMode === 'today' ? recentActivity : (localRecentActivity || []);
                return displayActivity.length > 0 ? displayActivity.map((event, index) => {
                  const IconComponent = event.event_type === 'open' ? Mail :
                                     event.event_type === 'click' ? MousePointer :
                                     event.event_type === 'reply' ? MessageSquare :
                                     event.event_type === 'bounce' ? TrendingUp : Activity;
                  
                  const eventColors = {
                    open: 'from-blue-500 to-blue-600',
                    click: 'from-green-500 to-green-600',
                    reply: 'from-purple-500 to-purple-600',
                    bounce: 'from-red-500 to-red-600'
                  };
                  
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.1 + index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-2xl hover:from-gray-100 hover:to-gray-50 transition-all duration-300 border border-gray-200/50 shadow-sm"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 bg-gradient-to-br ${eventColors[event.event_type] || 'from-gray-500 to-gray-600'} rounded-2xl flex items-center justify-center shadow-sm`}>
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 capitalize">{event.event_type.replace('_', ' ')}</p>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              event.event_type === 'reply' ? 'bg-purple-100 text-purple-700' :
                              event.event_type === 'open' ? 'bg-blue-100 text-blue-700' :
                              event.event_type === 'click' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {event.event_type.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-medium">{event.lead_email}</p>
                          {event.campaign_name && (
                            <p className="text-xs text-gray-500 mt-1">{event.campaign_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600 font-medium bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                          {new Date(event.created_at).toLocaleDateString()}
                        </div>
                        {event.score_delta && (
                          <div className={`text-xs mt-2 px-3 py-1 rounded-full font-medium ${
                            event.score_delta > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {event.score_delta > 0 ? '+' : ''}{event.score_delta} pts
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                }) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-4">
                      <Activity className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No activity for {viewMode === 'today' ? 'today' : selectedDate}</h3>
                    <p className="text-gray-600">{viewMode === 'today' ? 'Activity will appear here as leads engage with your campaigns.' : 'No activity found for this date. Try selecting another date.'}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Campaign Performance */}
      {campaignData.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-md border border-gray-200/60 p-5 hover:shadow-lg transition-all duration-200 relative overflow-hidden group min-h-[420px]"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-blue-500/10 opacity-60 group-hover:opacity-80 transition-opacity duration-200" />
          
          <div className="relative h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                  <Target className="w-4 h-4 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Top Campaign Performance</h3>
                  <p className="text-xs text-gray-600 font-medium">Highest performing campaigns by lead score</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-gray-700">LIVE</span>
              </div>
            </div>
            
            <div className="space-y-3 flex-1">
              {campaignData.map((campaign, index) => (
                <motion.div
                  key={campaign.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.05, ease: "easeOut" }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-white/80 to-gray-50/80 rounded-xl hover:from-gray-50/80 hover:to-gray-100/80 transition-all duration-200 border border-gray-200/40 backdrop-blur-sm group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200">
                      <Target className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-base leading-tight">{campaign.name}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-600 flex items-center gap-1 font-medium">
                          <Users className="w-3 h-3" strokeWidth={2.5} />
                          {campaign.leads} leads
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100/80 text-blue-700 text-xs font-semibold rounded-md border border-blue-200/50">
                          #{index + 1}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-gray-900 leading-tight">{campaign.avgScore}</div>
                    <div className="text-xs text-gray-600 font-semibold">Avg Score</div>
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full mt-1.5">
                      <div 
                        className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300" 
                        style={{ width: `${Math.min((campaign.avgScore / 100) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;